import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon, IconsEnum } from 'components/Icons';

import { BookingTicket } from 'features/carrello/carrelloContent/prenotazioniContent/types';
import { CarrelloFooterPrenotazioni } from 'features/carrello/carrelloContent/prenotazioniContent/carrelloFooterPrenotazioni';
import { SportTicketItem } from 'features/sport/components/sportTicket/components/sportTicketItem';
import calcoloSportTicket from 'features/sport/components/sportTicket/utils/calcoloCarrello/utils';
import styles from './PrenotazioniDetailContainer.module.scss';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useReactToPrint } from 'react-to-print';

export type PrenotazioniDetailContainerProps = {
  codiceSessione: string;
  removePrenotazione: Function;
  scadenzaCodiceSessione: string;
  bookingTicket: BookingTicket;
};

export const PrenotazioniDetailContainer = ({
  bookingTicket,
  codiceSessione,
  removePrenotazione,
  scadenzaCodiceSessione,
}: PrenotazioniDetailContainerProps) => {
  const pageStyle = `{ size: 2.5in 4in }`;
  const refTable = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [elapsing, setElapsing] = useState<number[]>([]);
  const [elapsed, setElapsed] = useState<boolean>(false);

  const labelScaduta = useGetLabelByKey('scaduta');
  const labelScadeTra = useGetLabelByKey('scade-tra');
  const labelPrenotazione = useGetLabelByKey('prenotazione');
  const labelCodicePrenotazione = useGetLabelByKey('codice-prenotazione');
  const { quotaTotale, puntata, possibileVincitaMax } = useMemo(() => {
    return calcoloSportTicket(bookingTicket);
  }, [bookingTicket]);

  const print = useReactToPrint({
    pageStyle: pageStyle,
    content: () => refTable.current,
  });

  useEffect(() => {
    const nowTime = new Date();
    const { dataOra: mostRecentBet } = (bookingTicket.avvenimenti as Array<{ dataOra: string }>).reduce(
      (closest, current) => {
        const closestTime = new Date(closest.dataOra).getTime();
        const currentTime = new Date(current.dataOra).getTime();
        const targetTime = new Date(nowTime).getTime();

        const closestDiff = Math.abs(targetTime - closestTime);
        const currentDiff = Math.abs(targetTime - currentTime);

        return currentDiff < closestDiff ? current : closest;
      }
    );
    const expireDate = new Date(scadenzaCodiceSessione);
    if (new Date(mostRecentBet).getTime() >= expireDate.getTime()) {
      // Calculate the time difference in milliseconds
      const timeDifferenceMs = new Date(expireDate).getTime() - nowTime.getTime();
      const hours = Math.floor(timeDifferenceMs / (1000 * 60 * 60));
      const minutes = Math.floor((timeDifferenceMs % (1000 * 60 * 60)) / (1000 * 60));
      hours < 0 && minutes < 0 && setElapsed(true);
      setElapsing([Math.abs(hours), Math.abs(minutes)]);
    } else {
      // Calculate the time difference in milliseconds
      const timeDifferenceMs = new Date(mostRecentBet).getTime() - nowTime.getTime();
      const hours = Math.floor(timeDifferenceMs / (1000 * 60 * 60));
      const minutes = Math.floor((timeDifferenceMs % (1000 * 60 * 60)) / (1000 * 60));
      hours < 0 && minutes < 0 && setElapsed(true);
      setElapsing([Math.abs(hours), Math.abs(minutes)]);
    }
  }, [bookingTicket.avvenimenti, scadenzaCodiceSessione]);

  const elapseRemainingTime = !elapsed ? (
    <React.Fragment>
      {elapsing[0]}
      <span>h</span> {elapsing[1]}
      <span>min</span>
    </React.Fragment>
  ) : null;

  return (
    <React.Fragment>
      <div className={styles.buttonWrapper}>
        <button type="button" className={styles.list} onClick={() => setIsOpen(!isOpen)}>
          <div className={`${styles.listContainer}`}>
            <div className={`${styles.listColumn1}`}>
              <div className={`${styles.listRow} ${styles.bookingCodeLabel}`}>{labelCodicePrenotazione}</div>
              <div className={`${styles.listRow} ${styles.bookingCode}`}>{bookingTicket.idPrenotazione}</div>
            </div>
            <div className={`${styles.listColumn2}`}>
              <div className={`${styles.remainingTime} ${elapsed && styles.elapsed}`}>
                {elapsed ? <span className={styles.elapsed}>{labelScaduta}</span> : '-'}
                {elapseRemainingTime}
              </div>
              <div className={styles.listCentered}>
                <Icon
                  iconId={isOpen ? IconsEnum.CHEVRON_LEFT : IconsEnum.CHEVRON_RIGHT}
                  className={styles.icon}
                  color="var(--color-on-bg-primary)"
                />
              </div>
            </div>
          </div>
        </button>
      </div>
      <div className={`${styles.container} ${isOpen && styles.visibile}`}>
        <div className={styles.displaceVertically}>
          <div className={styles.displaceVertically}>
            <div className={styles.header}>
              <button type="button" className={styles.list} onClick={() => setIsOpen(!isOpen)}>
                <Icon
                  iconId={isOpen ? IconsEnum.CHEVRON_LEFT : IconsEnum.CHEVRON_RIGHT}
                  className={styles.icon}
                  color="var(--color-primary)"
                />
                {labelPrenotazione}
              </button>
              <button type="button" className={styles.list} onClick={() => print()}>
                <Icon iconId={IconsEnum.DOWNLOAD} className={styles.iconBig} color="var(--color-on-bg-primary)" />
              </button>
            </div>
            <div ref={refTable} className={styles.displaceVertically}>
              <div className={styles.displaceVertically}>
                <div className={`${styles.listContainer} ${styles.listSummary}`}>
                  <div className={`${styles.listColumn1}`}>
                    <div className={`${styles.listRow} ${styles.bookingCodeLabel}`}>{labelCodicePrenotazione}</div>
                    <div className={`${styles.listRow} ${styles.bookingCode}`}>{bookingTicket.idPrenotazione}</div>
                  </div>
                  <div className={`${styles.listColumn1} ${styles.justifyRight}`}>
                    <div className={`${styles.listRow} ${styles.bookingCodeLabel}`}>{elapsed ? '' : labelScadeTra}</div>

                    {elapsed ? (
                      <div
                        className={`${styles.listRow} ${styles.remainingTime} ${elapsed && styles.elapsed} ${
                          styles.remainingTimeDetails
                        }`}
                      >
                        <span className={styles.elapsed}>{labelScaduta}</span>
                      </div>
                    ) : (
                      <div
                        className={`${styles.listRow} ${styles.remainingTime} ${elapsed && styles.elapsed} ${
                          styles.remainingTimeDetails
                        }`}
                      >
                        {elapseRemainingTime}
                      </div>
                    )}
                  </div>
                </div>

                {!!bookingTicket.avvenimenti?.length &&
                  bookingTicket.avvenimenti.map((avvenimento) => {
                    return (
                      <div key={avvenimento.id}>
                        <SportTicketItem avvenimento={avvenimento} cancelBtn={false} />
                      </div>
                    );
                  })}
              </div>
              <CarrelloFooterPrenotazioni
                totale={quotaTotale / 100}
                puntata={puntata / 100}
                vincitaPotenziale={possibileVincitaMax / 100}
                idPrenotazione={bookingTicket.idPrenotazione}
                codiceSessione={codiceSessione}
                removePrenotazione={removePrenotazione}
              />
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
